import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, authApi } from "@/lib/api";
import type { AdminUser } from "#shared/types";

export function AccountPanel({ me }: { me: AdminUser }) {
  const queryClient = useQueryClient();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [newAdmin, setNewAdmin] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);

  const { data: admins = [] } = useQuery({ queryKey: ["admins"], queryFn: adminApi.admins });

  const changePassword = useMutation({
    mutationFn: () => authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setRepeatPassword("");
      toast.success("Lösenordet är bytt. Andra enheter har loggats ut.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const createAdmin = useMutation({
    mutationFn: () => adminApi.createAdmin(newAdmin!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
      setNewAdmin(null);
      toast.success("Administratören är tillagd");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const removeAdmin = useMutation({
    mutationFn: adminApi.deleteAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admins"] });
      toast.success("Administratören är borttagen");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const mismatch = repeatPassword.length > 0 && newPassword !== repeatPassword;
  const tooShort = newPassword.length > 0 && newPassword.length < 10;

  return (
    <div className="space-y-5">
      <div className="surface p-5 sm:p-6">
        <h2 className="text-lg font-semibold">Ditt konto</h2>
        <p className="text-sm text-muted-foreground">
          {me.name} · {me.email}
        </p>

        <form
          className="mt-5 grid gap-4 sm:max-w-md"
          onSubmit={(event) => {
            event.preventDefault();
            changePassword.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="current-password">Nuvarande lösenord</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-password">Nytt lösenord</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            {tooShort && <p className="text-xs text-destructive">Minst 10 tecken.</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="repeat-password">Upprepa nytt lösenord</Label>
            <Input
              id="repeat-password"
              type="password"
              autoComplete="new-password"
              value={repeatPassword}
              onChange={(event) => setRepeatPassword(event.target.value)}
            />
            {mismatch && <p className="text-xs text-destructive">Lösenorden stämmer inte.</p>}
          </div>

          <Button
            type="submit"
            className="rounded-full sm:w-fit"
            disabled={
              changePassword.isPending || !currentPassword || newPassword.length < 10 || mismatch
            }
          >
            <KeyRound className="size-4" />
            {changePassword.isPending ? "Byter…" : "Byt lösenord"}
          </Button>
        </form>
      </div>

      <div className="surface p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Administratörer</h2>
            <p className="text-sm text-muted-foreground">
              De som kan logga in i adminläget och hantera personal och löneexport.
            </p>
          </div>
          <Button
            className="rounded-full"
            onClick={() => setNewAdmin({ name: "", email: "", password: "" })}
          >
            <Plus className="size-4" /> Lägg till
          </Button>
        </div>

        <ul className="mt-5 divide-y">
          {admins.map((admin) => (
            <li key={admin.id} className="flex items-center gap-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {admin.name}
                  {admin.id === me.id && (
                    <span className="ml-2 rounded-full bg-primary/12 px-2 py-0.5 text-xs text-primary">
                      Du
                    </span>
                  )}
                  {!admin.has_password && (
                    <span className="ml-2 rounded-full bg-warning/20 px-2 py-0.5 text-xs text-warning-foreground">
                      Lösenord saknas
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {admin.email}
                  {admin.last_login_at
                    ? ` · senast inloggad ${new Date(admin.last_login_at).toLocaleDateString("sv-SE")}`
                    : " · har aldrig loggat in"}
                </p>
              </div>

              {admin.id !== me.id && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full text-destructive"
                  aria-label={`Ta bort ${admin.name}`}
                  onClick={() => {
                    if (confirm(`Ta bort administratören ${admin.name}?`)) {
                      removeAdmin.mutate(admin.id);
                    }
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <Dialog open={!!newAdmin} onOpenChange={(open) => !open && setNewAdmin(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Ny administratör</DialogTitle>
          </DialogHeader>

          {newAdmin && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="admin-name">Namn</Label>
                <Input
                  id="admin-name"
                  value={newAdmin.name}
                  onChange={(event) => setNewAdmin({ ...newAdmin, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-email">E-postadress</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={newAdmin.email}
                  onChange={(event) => setNewAdmin({ ...newAdmin, email: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="admin-password">Lösenord</Label>
                <Input
                  id="admin-password"
                  type="password"
                  autoComplete="new-password"
                  value={newAdmin.password}
                  onChange={(event) => setNewAdmin({ ...newAdmin, password: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Minst 10 tecken. Personen kan byta det själv efter första inloggningen.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setNewAdmin(null)}>
              Avbryt
            </Button>
            <Button
              className="rounded-full"
              disabled={
                createAdmin.isPending ||
                !newAdmin?.name.trim() ||
                !newAdmin?.email.trim() ||
                (newAdmin?.password.length ?? 0) < 10
              }
              onClick={() => createAdmin.mutate()}
            >
              {createAdmin.isPending ? "Sparar…" : "Lägg till"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
